import { TokenizedExpression } from '../SearchBar';
import { getEndOfGroup } from '../SearchBar/group';
import { TreeExpression, IdTreeExpression } from './entities';
import { doesExist } from '@benzinga/utils';

export const TokenizedToTree = (expr: TokenizedExpression[]): TreeExpression | undefined => {
  let exprType: 'and' | 'or' | undefined = undefined;
  const stack: TreeExpression[] = [];
  for (let i = 0; i < expr.length; i++) {
    const token = expr[i];
    switch (token.operator) {
      case 'expression':
        stack.push({ ...token });
        break;
      case 'and':
      case 'or':
        if (exprType === undefined) {
          exprType = token.operator;
        } else if (exprType !== token.operator) {
          throw new Error(
            `TokenizedToTree Error: Operator should match at the top level of a group\n${i}\n${JSON.stringify(expr)}`,
          );
        }
        break;
      case 'group_start': {
        const j = getEndOfGroup({ expressions: expr, index: i });
        const result = TokenizedToTree(expr.slice(i + 1, j));
        if (result !== undefined) {
          stack.push(result);
        }
        // this will get incremented so we will never see group_end
        i = j;
        break;
      }
      case 'not_start': {
        const j = getEndOfGroup({ expressions: expr, index: i });
        const result = TokenizedToTree(expr.slice(i + 1, j));
        if (result !== undefined) {
          stack.push({ expr: result, operator: 'not' });
        }
        // this will get incremented so we will never see not_end
        i = j;
        break;
      }
      case 'group_end':
      case 'not_end':
        throw new Error(`TokenizedToTree Error: Unexpected end token\n${i}\n${JSON.stringify(expr)}`);
      case 'position':
        break;
    }
  }

  if (stack.length === 0) {
    return undefined;
  }
  if (stack.length === 1) {
    return stack[0];
  }
  if (exprType === undefined) {
    throw new Error(`TokenizedToTree Error: Unexpected missing operator\n${JSON.stringify(expr)}`);
  }
  return { exprs: stack, operator: exprType };
};

export const TreeToTokenized = <T>(expr: TreeExpression<T> | undefined, id = 'root'): TokenizedExpression<T>[] => {
  if (!expr) {
    return [];
  }
  switch (expr.operator) {
    case 'and':
    case 'or':
      return [
        { groupIndex: 9999, id: 'group_start_' + id, operator: 'group_start' },
        ...expr.exprs
          .flatMap((e, idx) => [
            ...TreeToTokenized(e, id + '_' + idx),
            { id: id + '_' + expr.operator + '_' + idx, operator: expr.operator },
          ])
          .slice(0, -1),
        { groupIndex: 9999, id: 'group_end_' + id, operator: 'group_end' },
      ];
    case 'not': {
      const convertedExpr = TreeToTokenized(expr.expr, id + '_not');
      if (convertedExpr.length === 1) {
        return [
          { groupIndex: 9999, id: id + '_not_start', operator: 'not_start' },
          convertedExpr[0],
          { groupIndex: 9999, id: id + '_not_end', operator: 'not_end' },
        ];
      }
      const exprStart = convertedExpr[0];
      const exprEnd = convertedExpr[convertedExpr.length - 1];
      if (exprStart.operator === 'group_start' && exprEnd.operator === 'group_end') {
        convertedExpr[0].operator = 'not_start';
        convertedExpr[0].id = id + '_not_start';
        convertedExpr[convertedExpr.length - 1].operator = 'not_end';
        convertedExpr[convertedExpr.length - 1].id = id + '_not_end';
        return convertedExpr;
      }

      if (exprStart.operator === 'not_start' && exprEnd.operator === 'not_end') {
        return [
          { groupIndex: 9999, id: id + '_not_start', operator: 'not_start' },
          ...convertedExpr,
          { groupIndex: 9999, id: id + '_not_end', operator: 'not_end' },
        ];
      }

      throw new Error('TreeToTokenized Error: Invalid not expression contents');
    }
    default:
      return [expr];
  }
};

export const TreeToIdTree = (expr?: TreeExpression, parentId?: string): IdTreeExpression | undefined => {
  if (!expr) {
    return undefined;
  }
  const uuid = window.crypto.randomUUID();
  switch (expr.operator) {
    case 'and':
    case 'or':
      return {
        exprs: expr.exprs.map(e => TreeToIdTree(e, uuid)).filter(doesExist),
        isGhost: false,
        operator: expr.operator,
        parentUuid: parentId,
        uuid,
      };
    case 'not':
      return {
        expr: TreeToIdTree(expr.expr, uuid),
        isGhost: false,
        operator: expr.operator,
        parentUuid: parentId,
        uuid,
      };
    case 'expression':
      return {
        ...expr,
        isGhost: false,
        parentUuid: parentId,
        uuid,
      };
  }
};

export const IdTreeToTree = (expr?: IdTreeExpression): TreeExpression | undefined => {
  if (!expr) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isGhost: _2, parentUuid: _0, uuid: _1, ...tree } = expr;

  switch (tree.operator) {
    case 'and':
    case 'or':
      return {
        ...tree,
        exprs: tree.exprs.map(e => IdTreeToTree(e)).filter(doesExist),
      };
    case 'not':
      return {
        ...tree,
        expr: IdTreeToTree(tree.expr),
      };
    case 'expression':
      return { ...tree };
    case 'incomplete_expression':
      if (tree.fallbackExpression) {
        return IdTreeToTree(tree.fallbackExpression);
      } else {
        return undefined;
      }
  }
};
